/**
 * 플레이리스트 복제 훅
 *
 * 기존 플레이리스트의 설정을 복사하여 새 플레이리스트를 생성합니다.
 * 트랙은 복사되지 않고 설정(이름, 장르, 분위기 등)만 복사됩니다.
 */

'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { invalidatePlaylistList } from '@/lib/api/cacheUtils';
import {
  usePlaylistCreate,
  getPlaylistRetrieveQueryKey,
} from '@/lib/api/orval';
import type { PlaylistDetail, PlaylistCreateRequest } from '@/lib/api/orval/models';

// ============================================================================
// Types
// ============================================================================

export interface DuplicatePlaylistRequest {
  playlistId: string;
  newName?: string;
}

interface UseDuplicatePlaylistOptions {
  onSuccess?: (data: PlaylistDetail) => void;
  onError?: (error: Error) => void;
}

// ============================================================================
// Hook
// ============================================================================

export function useDuplicatePlaylist(options?: UseDuplicatePlaylistOptions) {
  const queryClient = useQueryClient();
  const createPlaylist = usePlaylistCreate();

  return useMutation({
    mutationFn: async ({ playlistId, newName }: DuplicatePlaylistRequest) => {
      // 원본 플레이리스트 데이터 가져오기 (Orval 생성 캐시 키 사용)
      const sourcePlaylist = queryClient.getQueryData<PlaylistDetail>(
        getPlaylistRetrieveQueryKey(playlistId)
      );

      if (!sourcePlaylist) {
        throw new Error('원본 플레이리스트를 찾을 수 없습니다. 페이지를 새로고침 후 다시 시도해주세요.');
      }

      // 새 플레이리스트 데이터 생성
      const newPlaylistData: PlaylistCreateRequest = {
        name: newName || `${sourcePlaylist.name}의 사본`,
        genre: sourcePlaylist.genre || '',
        mood: sourcePlaylist.mood || undefined,
        description: sourcePlaylist.description || undefined,
        includeLyrics: sourcePlaylist.includeLyrics ?? false,
        generationPrompt: sourcePlaylist.generationPrompt || undefined,
        // 커버 이미지는 복사하지 않음 (새로 생성해야 함)
      };

      // 새 플레이리스트 생성
      return createPlaylist.mutateAsync({ data: newPlaylistData });
    },
    onSuccess: (data) => {
      invalidatePlaylistList(queryClient);
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      options?.onError?.(error);
    },
  });
}
